import { useAuth0 } from "@auth0/auth0-react";
import { InlineIcon } from "@iconify/react";
import _ from "lodash";
import PropTypes from "prop-types";
import { useContext, useEffect } from "react";
import { createUseStyles } from "react-jss";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import tinycolor from "tinycolor2";
import { AuthEnvContext, NoScrollContext } from "../../../../context";
import { useApoAuth } from "../../../../hooks";
import jssVariables from "../../../../jss_setup";
import { StyleTypeContext, TranslationContext } from "../../../../umbilical_tools";
import CurrencySelector from "../../../CurrencySelector";
import LanguageSelector from "../../../LanguageSelector";
import { GenericSectionsLinksMobile } from "../../../navbars/GenericSectionsLinks";
import { ProductTypeSectionsLinksMobile } from "../../../navbars/ProductTypeSectionsLinks";
import { LoginComp } from "./LoginComp";

const styles = createUseStyles({
  MenuModal: (props) => ({
    height: "100vh",
    width: "100vw",
    position: "fixed",
    top: 0,
    left: 0,
    background: jssVariables.colors.base_color,
    zIndex: 9999,
    ..._.get(props, "customStyles.MenuModal", {}),
  }),
  closeIconContr: (props) => ({
    display: "grid",
    justifyItems: "end",
    padding: jssVariables.normal_gap,
    ..._.get(props, "customStyles.closeIconContr", {}),
  }),
  closeIcon: (props) => ({
    color: "white",
    fontSize: "xxx-large",
    cursor: "pointer",
    ..._.get(props, "customStyles.closeIcon", {}),
  }),
  menuItemsSets: (props) => ({
    display: "grid",
    alignContent: "space-between",
    height: "80%",
    overflowY: "auto",
    zIndex: 2,
    paddingBottom: `calc(${jssVariables.navbar_height} * 2)`,
    ..._.get(props, "customStyles.menuItemsSets", {}),
  }),
  menuItems: (props) => ({
    display: "grid",
    gridAutoRows: "max-content",
    gridGap: jssVariables.double_gap,
    justifyItems: "center",
    alignItems: "center",
    paddingTop: jssVariables.double_gap,
    ..._.get(props, "customStyles.menuItems", {}),
  }),
  menuItem: (props) => ({
    cursor: "pointer",
    color: "white",
    fontSize: "large",
    fontWeight: "bold",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "none",
      color: "white",
    },
    ...(props?.customStyles?.menuItem ?? {}),
  }),
  bluredBottom: (props) => ({
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100vw",
    height: "25%",
    background: `linear-gradient(0deg, ${tinycolor(jssVariables.colors.base_color)
      .setAlpha(1)
      .toRgbString()} 50%, ${tinycolor(jssVariables.colors.base_color)
      .setAlpha(0.1)
      .toRgbString()} 100%)`,
    zIndex: 1,
    ..._.get(props, "customStyles.bluredBottom", {}),
  }),
});
export const MenuModal = ({
  closeModal,
  customStyles,
  home_btn_url,
  product_types,
  withLogin,
  languages,
  multi_currency,
  showCurrencyModal,
  showLanguageModal,
  setShowCurrencyModal,
  setShowLanguageModal,
}) => {
  const { brand } = useContext(StyleTypeContext);

  const classes = styles({ customStyles });

  const { toggleNoScroll } = useContext(NoScrollContext);
  const { t } = useContext(TranslationContext);

  // Toggle no scroll on mount and untoggle it on unmount
  useEffect(() => {
    toggleNoScroll();
    return () => toggleNoScroll();
  }, []);

  return (
    <div className={classes.MenuModal}>
      {/* <div className={classes.bluredBottom} /> */}
      <div className={classes.closeIconContr}>
        <InlineIcon
          icon="material-symbols:close"
          className={classes.closeIcon}
          onClick={closeModal}
        />
      </div>
      <div className={classes.menuItemsSets}>
        <div className={classes.menuItems}>
          {home_btn_url === "/" ? (
            <Link className={classes.menuItem} to={home_btn_url} onClick={closeModal}>
              <h4>{t("home")}</h4>
            </Link>
          ) : (
            <span onClick={() => (window.location.href = home_btn_url)}>
              <h4>{t("home")}</h4>
            </span>
          )}
          <GenericSectionsLinksMobile
            product_types={product_types}
            customStyles={customStyles}
            onHide={closeModal}
            closeModal={closeModal}
          />
          <ProductTypeSectionsLinksMobile
            product_types={product_types}
            customStyles={customStyles}
            onHide={closeModal}
          />
        </div>
        <div className={classes.menuItems}>
          {withLogin && !["thehotelco"].includes(brand) && (
            <ProfileComp hideMenu={closeModal} customStyles={customStyles} />
          )}
          {(languages ?? []).length > 1 && (
            <LanguageSelector
              languages={languages}
              customStyles={customStyles}
              showLanguageModal={showLanguageModal}
              setShowLanguageModal={setShowLanguageModal}
            />
          )}
          {multi_currency && (
            <CurrencySelector
              showCurrencyModal={showCurrencyModal}
              setShowCurrencyModal={setShowCurrencyModal}
            />
          )}
        </div>
      </div>
    </div>
  );
};
MenuModal.defaultProps = {};
MenuModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  customStyles: PropTypes.object,
  home_btn_url: PropTypes.string.isRequired,
  product_types: PropTypes.array.isRequired,
  withLogin: PropTypes.bool.isRequired,
  languages: PropTypes.array.isRequired,
  multi_currency: PropTypes.bool.isRequired,
  showCurrencyModal: PropTypes.bool.isRequired,
  showLanguageModal: PropTypes.bool.isRequired,
  setShowCurrencyModal: PropTypes.func.isRequired,
  setShowLanguageModal: PropTypes.func.isRequired,
};

const profileCompStyles = createUseStyles({
  ProfileComp: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content max-content",
    gridGap: jssVariables.normal_gap,
    alignItems: "center",
    color: "white",
    fontSize: "large",
    fontWeight: "bold",
    width: "max-content",
    position: "relative",
    ..._.get(props, "customStyles.ProfileComp", {}),
  }),
  avatar: (props) => ({
    width: jssVariables.double_gap,
    aspectRatio: "1/1",
    borderRadius: "50%",
    objectFit: "contain",
    ..._.get(props, "customStyles.avatar", {}),
  }),
  itemIcon: (props) => ({
    fontSize: "xx-large",
    ..._.get(props, "customStyles.itemIcon", {}),
  }),
});
export const ProfileComp = ({ customStyles, hideMenu }) => {
  const classes = profileCompStyles({ customStyles });

  const history = useHistory();

  const { t } = useContext(TranslationContext);

  const { user, loginWithRedirect } = useAuth0();
  const authCtx = useContext(AuthEnvContext);
  const { isAuthenticated } = useApoAuth();
  const user_picture = _.get(user, "picture");

  const unreadEventsByUsername = useSelector(
    (state: { ChatReducer: { unreadEventsByUsername: Set<string> } }) =>
      state.ChatReducer?.unreadEventsByUsername || new Set<string>(),
  );

  const unreadCount = unreadEventsByUsername.size;

  return (
    <div
      className={classes.ProfileComp}
      onClick={async () => {
        if (!isAuthenticated) {
          await loginWithRedirect({ redirect_uri: authCtx.redirectUrl });
        } else {
          history.push("/my_profile");
          hideMenu();
        }
      }}
    >
      {user && user_picture ? (
        <img src={user_picture} alt="user avatar" className={classes.avatar} />
      ) : (
        <InlineIcon icon="mingcute:user-4-fill" className={classes.itemIcon} />
      )}{" "}
      <h4>{isAuthenticated ? t("myProfile") : t("login")}</h4>
      {isAuthenticated && unreadCount > 0 && (
        <span className="absolute -right-2 -top-1 bg-red-500 text-white rounded-full min-w-[18px] h-[18px] flex items-center justify-center text-[11px] font-bold px-1 leading-none shadow-md">
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </div>
  );
};
ProfileComp.defaultProps = {};
ProfileComp.propTypes = {
  customStyles: PropTypes.object,
  hideMenu: PropTypes.func.isRequired,
};

export { LoginComp };
